"use client"

import { useState, useEffect } from "react"

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false)
  const [showManage, setShowManage] = useState(false)

  const [preferences, setPreferences] = useState({
    analytics: true,
    marketing: true,
  })

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent")
    if (!consent) {
      setTimeout(() => setShowBanner(true), 1500)
    }
  }, [])

  const handleAcceptAll = () => {
    localStorage.setItem("cookieConsent", JSON.stringify({ analytics: true, marketing: true }))
    setShowBanner(false)
    setShowManage(false)
  }

  const handleSavePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences))
    setShowBanner(false)
    setShowManage(false)
  }

  if (!showBanner && !showManage) return null

  return (
    <>
      {/* Main Bottom Banner */}
      {showBanner && !showManage && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border p-4 md:p-6 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] flex flex-col md:flex-row items-center justify-between gap-4 animate-in slide-in-from-bottom-6">
          <div className="text-sm font-medium flex-1 text-foreground/80">
            <strong className="font-black text-foreground uppercase tracking-widest mr-2">Our Promise:</strong> 
            We use cookies to serve you better. We respect your privacy, we don't sell your data, and you are always in control.
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button 
              onClick={() => setShowManage(true)} 
              className="flex-1 md:flex-none px-4 py-2 border border-border rounded-sm hover:bg-muted font-bold tracking-widest text-xs uppercase transition-colors"
            >
              Manage Settings
            </button>
            <button 
              onClick={handleAcceptAll} 
              className="flex-1 md:flex-none px-6 py-2 bg-brand text-white rounded-sm hover:bg-brand/90 font-bold tracking-widest text-xs uppercase transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      )}

      {/* Manage Settings Modal */}
      {showManage && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-background border border-border shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-sm animate-in zoom-in-95">
            <div className="p-6 md:p-8 space-y-6">
              
              <div className="space-y-2">
                <h2 className="text-2xl font-black uppercase tracking-tight">We respect you.</h2>
                <p className="text-sm text-muted-foreground leading-relaxed font-serif italic">
                  We are journalists, not data brokers. We do not steal, snoop, or secretly sell your personal data. The cookies we use are strictly to keep the platform fast, understand which reports you value, and show relevant ads to support our independent newsroom.
                </p>
              </div>

              <div className="space-y-4 pt-4 border-t border-border">
                {/* Essential */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-sm">Strictly Necessary</h3>
                    <p className="text-xs text-muted-foreground mt-1">Required for the website to function securely. These cannot be switched off.</p>
                  </div>
                  <div className="shrink-0">
                    <span className="text-xs font-bold text-brand uppercase tracking-widest bg-brand/10 px-2 py-1 rounded-sm">Always On</span>
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-start justify-between gap-4 pt-4 border-t border-border/50">
                  <div>
                    <h3 className="font-bold text-sm">Analytics & Performance</h3>
                    <p className="text-xs text-muted-foreground mt-1">Helps us understand reader trends and improve our editorial coverage.</p>
                  </div>
                  <div className="shrink-0">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
                    </label>
                  </div>
                </div>

                {/* Personalization (Dummy) */}
                <div className="flex items-start justify-between gap-4 pt-4 border-t border-border/50">
                  <div>
                    <h3 className="font-bold text-sm">Editorial Personalization</h3>
                    <p className="text-xs text-muted-foreground mt-1">Allows us to remember your reading preferences (like dark mode) and regional interests.</p>
                  </div>
                  <div className="shrink-0">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        defaultChecked={true}
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-6">
                <button 
                  onClick={handleSavePreferences}
                  className="flex-1 px-4 py-3 border border-border rounded-sm hover:bg-muted font-bold tracking-widest text-xs uppercase transition-colors"
                >
                  Save My Choices
                </button>
                <button 
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-3 bg-brand text-white rounded-sm hover:bg-brand/90 font-bold tracking-widest text-xs uppercase transition-colors"
                >
                  Accept All
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  )
}
